import { ChevronLeft } from 'lucide-react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import { useUser } from '../auth/context'
import { useBack, useTelegramBackButton } from '../lib/useBack'
import { roleLabel } from '../lib/team'
import { useIsDesktop } from '../lib/useIsDesktop'
import { useViewCanGoBack } from '../lib/navigation'
import { ANALYTICS, SECTIONS, SETTINGS, isRootPath, titleFor } from '../nav'
import { hasTelegramBack } from '../telegram'
import { Avatar } from './Avatar'
import { Logo } from './Logo'

export function AppShell() {
  const desktop = useIsDesktop()
  useTelegramBackButton()
  return desktop ? <DesktopShell /> : <MobileShell />
}

// ≥768px в браузере: боковое меню и рабочая область.
function DesktopShell() {
  const user = useUser()
  const navItem = ({ isActive }: { isActive: boolean }) =>
    `flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] transition-colors ${
      isActive ? 'bg-surface font-semibold text-link ring-1 ring-line' : 'text-muted hover:bg-line/50 hover:text-fg'
    }`

  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 flex h-dvh w-56 shrink-0 flex-col border-r border-line bg-card px-3 pt-4 pb-3">
        <Link to="/" className="mb-5 flex h-8 items-center rounded-md px-1">
          <Logo />
        </Link>
        <nav className="flex flex-col gap-0.5" aria-label="Разделы">
          {SECTIONS.map(({ path, title, icon: Icon }) => (
            <NavLink key={path} to={path} end={path === '/'} className={navItem}>
              <Icon size={16} strokeWidth={2} />
              {title}
            </NavLink>
          ))}
          {user.role === 'owner' && <NavLink to={ANALYTICS.path} className={navItem}><ANALYTICS.icon size={16} strokeWidth={2} />{ANALYTICS.title}</NavLink>}
        </nav>
        <div className="mt-auto flex flex-col gap-0.5 border-t border-line pt-3">
          <NavLink to={SETTINGS.path} className={navItem}>
            <SETTINGS.icon size={16} strokeWidth={2} />
            {SETTINGS.title}
          </NavLink>
          <Link
            to={SETTINGS.path}
            className="mt-1 flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-line/50"
          >
            <Avatar name={user.name} src={user.photo_url} size={28} />
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[13px] font-medium">{user.name}</span>
              <span className="block truncate text-xs text-muted">{roleLabel(user)}</span>
            </span>
          </Link>
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-6 pt-6 pb-10">
        <Outlet />
      </main>
    </div>
  )
}

// Телефон и Mini App: шапка сверху, вкладки снизу.
function MobileShell() {
  const user = useUser()
  const { pathname } = useLocation()
  const back = useBack()
  const hasPrevious = useViewCanGoBack()
  const nested = !isRootPath(pathname) || hasPrevious
  // В Telegram (6.1+) «Назад» системная, своя стрелка — в браузере и у старых клиентов
  const showBack = nested && !hasTelegramBack()

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-20 border-b border-line bg-chrome-top/95 pt-safe">
        <div className="flex h-12 items-center gap-1 px-safe">
          {showBack && (
            <button
              type="button"
              onClick={back}
              aria-label="Назад"
              className="-ml-3 inline-flex size-11 items-center justify-center rounded-full text-fg active:bg-surface"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <div className="min-w-0 flex-1">
            {!isRootPath(pathname) ? <span className="block truncate text-sm font-semibold">{titleFor(pathname)}</span> : <Logo />}
          </div>
          {pathname !== SETTINGS.path && (
            <Link
              to={SETTINGS.path}
              aria-label="Настройки и профиль"
              className="-mr-1.5 inline-flex size-11 items-center justify-center rounded-full active:bg-surface"
            >
              <Avatar name={user.name} src={user.photo_url} size={32} />
            </Link>
          )}
        </div>
      </header>

      <main className="mobile-content min-w-0 flex-1 px-safe pt-4">
        <Outlet />
      </main>

      <nav
        aria-label="Разделы"
        className="mobile-dock fixed z-20"
      >
        <div className="grid grid-cols-5">
          {SECTIONS.map(({ path, short, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) =>
                `flex min-h-12 min-w-0 flex-col items-center justify-center gap-0.5 rounded-full text-[9px] leading-none font-semibold ${
                  isActive ? 'text-link' : 'text-muted'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="mobile-dock-icon">
                    <Icon size={19} strokeWidth={isActive ? 2.25 : 1.75} aria-hidden />
                  </span>
                  <span className="max-w-full truncate">{short}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
